import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Box, Button, IconButton, Typography, Stepper, Step, StepLabel } from "@mui/material";

import APP_ROUTES from "../../../../routers/routeConstants";
import { getCreateServiceRoute } from "../../../../routers/routeUtils";

import {
  ArrowBackButtonIcon,
  CreateNowCardIcon,
  ProvideOverallStepperIcon,
  CompleteIdentityStepperIcon,
  AcceptJobsStepperIcon,
} from "../../assets/icons/create-service-icons";
import ServiceCardFigure from "../../assets/images/create-service-card-icon.png";
import ProgramCardFigure from "../../assets/images/create-program-card-icon.png";

import CreateService from "./CreateService";
import CreateProgram from "./CreateProgram";
import ServiceCreatedPopUp from "./ServiceCreatedPopUp";

const CreateServicePage = () => {
  const initialServiceCreatedState = {
    serviceCreated: false,
    serviceId: "",
    serviceType: "",
  };
  const [serviceCreatedPopUpOpen, setServiceCreatedPopUpOpen] = useState(initialServiceCreatedState);
  const handleServiceCreatedPopUp = ({
    serviceCreated,
    serviceId,
    serviceType,
  }: {
    serviceCreated: boolean;
    serviceId: string;
    serviceType: string;
  }) => setServiceCreatedPopUpOpen({ serviceCreated, serviceId, serviceType });
  const handleServiceCreatedPopUpClose = () => setServiceCreatedPopUpOpen(initialServiceCreatedState);

  const { state } = useLocation();
  const { serviceCreated = false, serviceId, serviceType } = state || {};

  const navigate = useNavigate();

  useEffect(() => {
    if (serviceCreated === true) {
      handleServiceCreatedPopUp({ serviceCreated, serviceId, serviceType });
      navigate("", { replace: true });
    }
  }, [serviceCreated, navigate]);

  const { t, i18n } = useTranslation("goService", {
    keyPrefix: "createServicePage",
  });
  const handleClickOnBackButton = () => {
    navigate(APP_ROUTES.OVERVIEW_SERVICES);
  };

  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");

  if (type === "service") return <CreateService />;
  if (type === "program") return <CreateProgram />;

  const serviceCards = [
    {
      title: t("service"),
      description: t("offerAOneTimeServiceOfferedToFootballProfessional"),
      figure: ServiceCardFigure,
      action: () => navigate(getCreateServiceRoute("service")),
    },
    {
      title: t("program"),
      description: t("designATailOrderProgramForTalentsAndProfessionalToRegisterInAndFinishInTheirOwn"),
      figure: ProgramCardFigure,
      action: () => navigate(getCreateServiceRoute("program")),
    },
  ];

  const steps = [
    {
      title: t("provideAnOverallOverviewAndDetailsOfYourServices"),
      icon: ProvideOverallStepperIcon,
    },
    {
      title: t("completeIdentityAndPaymentVerificationToPublishYourServices"),
      icon: CompleteIdentityStepperIcon,
    },
    {
      title: t("acceptJobsAndReceivePayment"),
      icon: AcceptJobsStepperIcon,
    },
  ];

  return (
    <>
      <Box sx={styles.container} component="main">
        <IconButton onClick={handleClickOnBackButton} sx={styles.backButton}>
          <ArrowBackButtonIcon sx={styles.backButtonIcon(i18n.language === "ar")} />
        </IconButton>
        <Box sx={styles.contentContainer}>
          <Box sx={styles.titleContainer}>
            <Typography sx={styles.title}>{t("createYourNewService")}</Typography>
            <Typography sx={styles.description}>{t("chooseTheTypeOfServiceYouWantToCreate")}</Typography>
          </Box>
          <Box sx={styles.cardsContainer}>
            {serviceCards.map(({ title, description, figure, action }, idx) => (
              <Box key={idx} sx={styles.card}>
                <Box sx={styles.cardFigure} component="img" src={figure} alt={title} />
                <Typography sx={styles.cardTitle}>{title}</Typography>
                <Typography sx={styles.cardDescription} title={description}>
                  {description}
                </Typography>
                <Button
                  sx={styles.cardButton(i18n.language === "ar")}
                  variant="text-md"
                  onClick={action}
                  endIcon={<CreateNowCardIcon />}
                >
                  {t("createNow")}
                </Button>
              </Box>
            ))}
          </Box>
        </Box>
        <Box sx={styles.howItWorksContainer}>
          <Box sx={styles.howItWorksTitleContainer}>
            <Typography sx={styles.howItWorksTitle}>{t("howItWorks")}</Typography>
            <Box sx={styles.setUpYourServicesTitleContainer}>
              <Typography sx={styles.setUpYourServicesTitle}>{t("setUpYourServices")}</Typography>
              <Typography sx={styles.setUpYourServicesDescription}>
                {t("toCompleteYourServiceFollowTheStepsOutlinedBelow")}
              </Typography>
            </Box>
          </Box>
          <Box sx={styles.stepperContainer}>
            <Stepper alternativeLabel activeStep={3} orientation="horizontal">
              {steps.map(({ title, icon }, idx) => (
                <Step key={idx}>
                  <StepLabel StepIconComponent={icon}>
                    <Typography sx={styles.stepLabel}>{title}</Typography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        </Box>
      </Box>
      <ServiceCreatedPopUp serviceCreatedState={serviceCreatedPopUpOpen} closeModal={handleServiceCreatedPopUpClose} />
    </>
  );
};

export default CreateServicePage;

const styles = {
  container: {
    width: "100%",
    borderRadius: "8px",
    border: "1px solid #EAECF0",
    background: "var(--Base-White, #FFF)",
    padding: { xs: "40px 20px", sm: "95px 105px" },
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "64px",
  },
  backButton: {
    position: "absolute",
    top: "25px",
    left: "16px",
  },
  backButtonIcon: (isArabic: boolean) => ({ transform: isArabic ? "scaleX(-1)" : "scaleX(1)" }),
  contentContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "40px",
  },
  titleContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: "3px",
  },
  title: {
    fontFamily: "Montserrat",
    color: "#1D1D1D",
    textAlign: "center",
    fontSize: "32px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "normal",
  },
  description: {
    fontFamily: "Montserrat",
    color: "#232323",
    textAlign: "center",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "150%",
  },
  cardsContainer: {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    gap: "16px",
  },
  card: {
    width: { xs: "100%", sm: "203px" },
    flex: 1,
    borderRadius: "16px",
    border: "1px solid #EAECF0",
    background: "var(--White, #FFF)",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "16px",
    padding: "16px",
  },
  cardFigure: {
    width: "44px",
    height: "44px",
  },
  cardTitle: {
    color: "var(--black-100, #1C1C1C)",
    fontFamily: "Montserrat",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: 750,
    lineHeight: "normal",
  },
  cardDescription: {
    fontFamily: "Montserrat",
    color: "var(--Gray---400, #A0A0A0)",
    fontSize: "13px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "normal",
    lineClamp: 3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
  },
  cardButton: (isArabic: boolean) => ({
    width: "fit-content",
    "& .MuiButton-endIcon": {
      transform: isArabic ? "scaleX(-1)" : "scaleX(1)",
    },
  }),
  howItWorksContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: "40px",
  },
  howItWorksTitleContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  howItWorksTitle: {
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    textAlign: "center",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: 800,
    lineHeight: "normal",
  },
  setUpYourServicesTitleContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  setUpYourServicesTitle: {
    fontFamily: "Montserrat",
    color: "var(--black-100, #1C1C1C)",
    textAlign: "center",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "normal",
  },
  setUpYourServicesDescription: {
    fontFamily: "Montserrat",
    color: "var(--Dark---600, #707070)",
    textAlign: "center",
    fontSize: "13px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "normal",
  },
  stepperContainer: {
    width: "100%",
    "& .MuiStepConnector-line": {
      borderColor: "#008B07",
      borderTopWidth: "1px",
    },
    "& .MuiStepConnector-root": {
      left: "calc(-50% + 14px)",
      right: "calc(50% + 14px)",
    },
  },
  stepLabel: {
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    textAlign: "center",
    fontSize: "13px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "normal",
  },
};
